import React, { useEffect, useState } from "react";
import "./AuctionPosts.scss";
import { FontApi } from "../../../../utils/api/Font";
import BottomSaveButton from "../../buttons/BottomSaveButton";
import toast from "react-hot-toast";
import { LocalStorageHelpers } from "../../../../utils/helpers/localstorage.helpers";

type MomentumOption = "standard" | "extendedExposure";
type FinalCallOption = "last3" | "last7";

const AuctionPosts = () => {
  const [momentum, setMomentum] = useState<MomentumOption>("standard");
  const [finalCallEnabled, setFinalCallEnabled] = useState<boolean>(false);
  const [finalCall, setFinalCall] = useState<FinalCallOption>("last3");

  const [initialMomentum, setInitialMomentum] =
    useState<MomentumOption>("standard");
  const [initialFinalCallEnabled, setInitialFinalCallEnabled] =
    useState<boolean>(false);
  const [initialFinalCall, setInitialFinalCall] =
    useState<FinalCallOption>("last3");

  const user = LocalStorageHelpers.getUserData();

  const isAgent = !!(user && "role" in user && user.role === "agent");

  const getId = (val: any) => {
    if (!val) return "";
    if (typeof val === "object") return val._id || val.$oid || "";
    return val;
  };

  const agencyId = isAgent
    ? getId((user as any)?.agency_ref)
    : getId((user as any)?._id);

  const [state, setState] = useState({
    isLoading: false,
    saveIsLoading: false,
    saveBtn: false,
  });

  const fetchData = async () => {
    setState((p) => ({ ...p, isLoading: true }));

    const res = await FontApi.GetAgencySettings({
      agency_id: agencyId,
    });

    if (res.success) {
      const auction = res.data?.auction_posts;

      const m: MomentumOption =
        auction?.campaign_momentum === "extended_exposure"
          ? "extendedExposure"
          : "standard";

      const fEnabled = !!auction?.final_call?.enabled;

      const fType: FinalCallOption =
        auction?.final_call?.type === "last_seven_days" ? "last7" : "last3";

      setMomentum(m);
      setFinalCallEnabled(fEnabled);
      setFinalCall(fType);

      setInitialMomentum(m);
      setInitialFinalCallEnabled(fEnabled);
      setInitialFinalCall(fType);
    } else {
      toast.error(res.error || "Failed to fetch settings");
    }

    setState((p) => ({ ...p, isLoading: false }));
  };

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    const changed =
      momentum !== initialMomentum ||
      finalCallEnabled !== initialFinalCallEnabled ||
      finalCall !== initialFinalCall;

    setState((p) => ({ ...p, saveBtn: changed }));
  }, [
    momentum,
    finalCallEnabled,
    finalCall,
    initialMomentum,
    initialFinalCallEnabled,
    initialFinalCall,
  ]);

  const onSubmitHandler = async () => {
    const payload = {
      auction_posts: {
        campaign_momentum:
          momentum === "extendedExposure" ? "extended_exposure" : "standard",
        final_call: {
          enabled: finalCallEnabled,
          type: finalCall === "last7" ? "last_seven_days" : "last_three_days",
        },
      },
    };

    setState((p) => ({ ...p, saveIsLoading: true }));

    const res = await FontApi.UpdateAgencySettings(payload);

    if (res.success) {
      toast.success("Auction post settings updated");

      setInitialMomentum(momentum);
      setInitialFinalCallEnabled(finalCallEnabled);
      setInitialFinalCall(finalCall);

      setState((p) => ({ ...p, saveIsLoading: false, saveBtn: false }));
    } else {
      toast.error(res.error || "Failed to update settings");
      setState((p) => ({ ...p, saveIsLoading: false }));
    }
  };

  const onClickDecline = () => {
    setMomentum(initialMomentum);
    setFinalCallEnabled(initialFinalCallEnabled);
    setFinalCall(initialFinalCall);
  };

  return (
    <div className="auction-post-settings">
      <h1 className="page-title">Auction post settings</h1>

      <section className="settings-section">
        <h2 className="section-title">Auction campaign type</h2>
        <p className="section-description">
          These settings control how often the property is promoted on social
          media leading up to auction day
        </p>

        <div
          className="cards-row"
          role="radiogroup"
          aria-label="Campaign momentum"
        >
          <label
            className={`option-card ${momentum === "standard" ? "active" : ""}`}
          >
            <input
              type="radio"
              name="momentum"
              value="standard"
              checked={momentum === "standard"}
              onChange={() => setMomentum("standard")}
              disabled={isAgent}
            />
            <div className="card-content">
              <div className="card-text">
                <div className="card-title">Standard (2 posts per week)</div>
                <div className="card-subtitle">
                  Best for a standard 4-week campaign
                </div>
              </div>
              <span
                className={`radio-dot ${momentum === "standard" ? "checked" : ""}`}
              />
            </div>
          </label>

          <label
            className={`option-card ${momentum === "extendedExposure" ? "active" : ""}`}
          >
            <input
              type="radio"
              name="momentum"
              value="extendedExposure"
              checked={momentum === "extendedExposure"}
              onChange={() => setMomentum("extendedExposure")}
              disabled={isAgent}
            />
            <div className="card-content">
              <div className="card-text">
                <div className="card-title">
                  Extended Exposure (3 posts per week)
                </div>
                <div className="card-subtitle">
                  Increased visibility leading up to auction day
                </div>
              </div>
              <span
                className={`radio-dot ${momentum === "extendedExposure" ? "checked" : ""}`}
              />
            </div>
          </label>
        </div>

        <p className="helper-note">
          *We will automatically select peak engagement times for your posts.
        </p>
      </section>

      <section className="settings-section final-call-section">
        <div className="title-with-switch">
          <h2 className="section-title">The "Final Call" Countdown</h2>

          <button
            type="button"
            className={`switch ${finalCallEnabled ? "on" : "off"}`}
            role="switch"
            aria-checked={finalCallEnabled}
            onClick={() => {
              if (isAgent) return;
              setFinalCallEnabled((p) => !p);
            }}
          >
            <span className="switch-thumb" />
          </button>
        </div>

        <div className="final-call-content">
          <p className="section-description">
            Select how often the property is featured across social media
            leading up to the auction day.
          </p>

          <div
            className={`cards-row ${finalCallEnabled ? "" : "disabled"}`}
            role="radiogroup"
            aria-label='The "Final Call" Countdown options'
          >
            <label
              className={`option-card ${finalCall === "last3" ? "active" : ""}`}
            >
              <input
                type="radio"
                name="finalCall"
                value="last3"
                checked={finalCall === "last3"}
                onChange={() => setFinalCall("last3")}
                disabled={!finalCallEnabled || isAgent}
              />
              <div className="card-content">
                <div className="card-text">
                  <div className="card-title">Last 3 days</div>
                  <div className="card-subtitle">
                    Adds a high-urgency post for the 3 days leading up to the
                    auction.
                  </div>
                </div>
                <span
                  className={`radio-dot ${finalCall === "last3" ? "checked" : ""}`}
                />
              </div>
            </label>

            <label
              className={`option-card ${finalCall === "last7" ? "active" : ""}`}
            >
              <input
                type="radio"
                name="finalCall"
                value="last7"
                checked={finalCall === "last7"}
                onChange={() => setFinalCall("last7")}
                disabled={!finalCallEnabled || isAgent}
              />
              <div className="card-content">
                <div className="card-text">
                  <div className="card-title">Last 7 days</div>
                  <div className="card-subtitle">
                    Adds a high-urgency post for the 7 days leading up to the
                    auction.
                  </div>
                </div>
                <span
                  className={`radio-dot ${finalCall === "last7" ? "checked" : ""}`}
                />
              </div>
            </label>
          </div>
        </div>
      </section>

      {!isAgent && (
        <BottomSaveButton
          is_loading={state.saveIsLoading}
          cancle_button_name="Decline"
          save_button_name="Save Changes"
          button_hide={state.saveBtn}
          onCancel={onClickDecline}
          submitButtonType={"button"}
          onSubmitClick={onSubmitHandler}
        />
      )}
    </div>
  );
};

export default AuctionPosts;
